import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { OrderListItemDTO } from '@wf/shared'
import { ApiError } from '../api/client'
import { minToTime } from '../orders/schedule'
import { usePatchOrder } from '../orders/useOrders'
import { parseTimeToMin } from './planung'

/**
 * Termin + Arbeitsplatz zuweisen (MVP der Plantafel: Dialog statt Drag&Drop). Ein OCC-Patch mit nur den
 * geänderten Feldern (Muster OrderMetaEdit); `skipped[]` und 409-Konflikt werden ehrlich angezeigt, der
 * Hook lädt bei Fehler den frischen Stand nach. Erfolgreich ohne skips → Dialog schließt.
 */
export function AssignDialog({
  order,
  day,
  bays,
  canPlan,
  onClose,
}: {
  order: OrderListItemDTO
  /** Angezeigter Tag der Plantafel — Vorbelegung für unterminierte Aufträge. */
  day: string
  bays: number
  /** Schreibrecht (annahme- oder belegung-Bearbeiten) — ohne bleibt der Dialog reine Ansicht. */
  canPlan: boolean
  onClose: () => void
}) {
  const patch = usePatchOrder(order.id)
  const [date, setDate] = useState(order.scheduledDate ?? day)
  const [start, setStart] = useState(order.startMin != null ? minToTime(order.startMin) : '')
  const [end, setEnd] = useState(order.endMin != null ? minToTime(order.endMin) : '')
  const [bay, setBay] = useState(order.bay != null ? String(order.bay) : '')
  const [formError, setFormError] = useState<string | null>(null)

  // Escape schließt (Leichtgewicht-Dialog ohne Fokus-Falle — bewusster MVP-Zuschnitt).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const nextDate = date || null
  const nextStart = start ? parseTimeToMin(start) : null
  const nextEnd = end ? parseTimeToMin(end) : null
  const nextBay = bay ? Number(bay) : null
  const dirty =
    nextDate !== order.scheduledDate ||
    nextStart !== (order.startMin ?? null) ||
    nextEnd !== (order.endMin ?? null) ||
    nextBay !== (order.bay ?? null)

  function save() {
    setFormError(null)
    if ((start && nextStart == null) || (end && nextEnd == null)) {
      setFormError('Ungültige Uhrzeit (HH:MM).')
      return
    }
    if ((nextStart == null) !== (nextEnd == null)) {
      setFormError('Bitte Start UND Ende angeben (oder beide leeren).')
      return
    }
    if (nextStart != null && nextEnd != null && nextEnd <= nextStart) {
      setFormError('Das Ende muss nach dem Start liegen.')
      return
    }
    if (nextBay != null && nextDate == null) {
      setFormError('Ein Arbeitsplatz braucht einen Termin-Tag.')
      return
    }
    if (!dirty) return
    patch.mutate(
      {
        version: order.version,
        ...(nextDate !== order.scheduledDate ? { scheduledDate: nextDate } : {}),
        ...(nextStart !== (order.startMin ?? null) ? { startMin: nextStart } : {}),
        ...(nextEnd !== (order.endMin ?? null) ? { endMin: nextEnd } : {}),
        ...(nextBay !== (order.bay ?? null) ? { bay: nextBay } : {}),
      },
      { onSuccess: (data) => (data.skipped.length === 0 ? onClose() : undefined) },
    )
  }

  const skipped = patch.data?.skipped ?? []
  const conflict = patch.error instanceof ApiError && patch.error.status === 409 ? patch.error.message : null
  const otherError = patch.error instanceof ApiError && patch.error.status !== 409 ? patch.error.message : null
  const title = [order.nr, order.plate].filter(Boolean).join(' · ') || `Auftrag #${order.id}`

  return (
    <div className="wf-plandlg" role="presentation" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="wf-plandlg__panel" role="dialog" aria-modal="true" aria-label={`Einplanen: ${title}`}>
        <div className="wf-plandlg__head">
          <strong>{title}</strong>
          <button type="button" className="wf-btn wf-btn--ghost" onClick={onClose} aria-label="Schließen">
            ✕
          </button>
        </div>
        {order.customerName ? <p className="sub">{order.customerName}</p> : null}

        <div className="two">
          <label className="wf-field">
            <span className="wf-label">Termin-Tag</span>
            <input className="wf-input" type="date" value={date} disabled={!canPlan} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="wf-field">
            <span className="wf-label">Arbeitsplatz</span>
            <select className="wf-input" value={bay} disabled={!canPlan} onChange={(e) => setBay(e.target.value)}>
              <option value="">— nicht zugeordnet —</option>
              {Array.from({ length: bays }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  Platz {i + 1}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="two">
          <label className="wf-field">
            <span className="wf-label">Von</span>
            <input className="wf-input" type="time" value={start} disabled={!canPlan} onChange={(e) => setStart(e.target.value)} />
          </label>
          <label className="wf-field">
            <span className="wf-label">Bis</span>
            <input className="wf-input" type="time" value={end} disabled={!canPlan} onChange={(e) => setEnd(e.target.value)} />
          </label>
        </div>

        {formError ? (
          <p className="error" role="alert">
            {formError}
          </p>
        ) : null}
        {skipped.length > 0 ? (
          <p className="warn" role="status">
            Nicht übernommen: {skipped.join(', ')} — Recht fehlt oder Platz existiert nicht.
          </p>
        ) : null}
        {conflict ? (
          <p className="warn" role="alert">
            Konflikt: {conflict} – der aktuelle Stand wurde neu geladen.
          </p>
        ) : null}
        {otherError ? (
          <p className="error" role="alert">
            {otherError}
          </p>
        ) : null}

        <div className="wf-plandlg__actions">
          <Link to={`/orders/${order.id}`} className="wf-btn wf-btn--ghost">
            Auftrag öffnen
          </Link>
          {canPlan ? (
            <button type="button" className="wf-btn wf-btn--primary" disabled={!dirty || patch.isPending} onClick={save}>
              {patch.isPending ? 'Speichern…' : 'Speichern'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
